import React, { useState, useEffect } from 'react';
import './TeacherClasses.css';
import DashboardHeader from '../components/DashboardHeader';
import TeacherNavigationBar from '../components/TeacherNavigationBar';

function TeacherClasses() {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [showClassModal, setShowClassModal] = useState(false);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const teacherId = localStorage.getItem('teacher_id');
      if (!teacherId) {
        console.error('ID du professeur non trouvé');
        setClasses([]);
        setLoading(false);
        return;
      }

      // Charger les statistiques du professeur qui incluent ses classes assignées
      const response = await fetch(`http://127.0.0.1:8000/api/v1/teacher/dashboard/statistiques?teacher_id=${teacherId}&annee_scolaire=2025-2026`);
      const data = await response.json();

      if (response.ok && data.classes) {
        setClasses(data.classes || []);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async (className) => {
    setLoading(true);
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/classes/${encodeURIComponent(className)}/etudiants`);
      const data = await response.json();

      if (response.ok) {
        const studentsData = Array.isArray(data) ? data : data.data || [];
        setStudents(studentsData);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = (classInfo) => {
    setSelectedClass(classInfo);
    loadClassStudents(classInfo.nom || classInfo.name);
  };

  const filteredStudents = students.filter(student =>
    (student.nom + ' ' + student.prenom).toLowerCase().includes(filterSearch.toLowerCase())
  );

  return (
    <div className="teacher-classes">
      <DashboardHeader />
      <TeacherNavigationBar 
        title="Mes Classes" 
        onBack={() => window.navigateTo && window.navigateTo('teacher-dashboard')}
      />
      <div className="classes-container">
        <main className="classes-main admin-content-full">
          <div className="classes-header" style={{display: 'none'}}>
            <h1>Mes Classes</h1>
            <p>Gérez vos classes et consultez la liste des élèves</p>
          </div>

          <div className="classes-content">
            <div className="classes-list">
              <h2>Vos Classes Assignées</h2>
              {loading && !selectedClass ? (
                <div className="loading">Chargement des classes...</div>
              ) : classes.length === 0 ? (
                <div className="empty-classes-state">
                  <div className="empty-icon">📚</div>
                  <h3>Aucune classe assignée</h3>
                  <p>Vous n'avez pas encore de classes assignées.</p>
                  <p>Contactez l'administration pour qu'elle vous assigne des classes depuis le dashboard admin.</p>
                  <div className="info-box">
                    <strong>💡 Comment ça marche :</strong>
                    <ul>
                      <li>L'admin va dans "Gestion des enseignants"</li>
                      <li>Il vous assigne des classes</li>
                      <li>Vos classes apparaîtront ici automatiquement</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="classes-grid">
                  {classes.map(classInfo => (
                    <div
                      key={classInfo.id}
                      className={`class-card ${selectedClass?.id === classInfo.id ? 'active' : ''}`}
                      onClick={() => handleClassSelect(classInfo)}
                    >
                      <div className="class-card-icon">👥</div>
                      <div className="class-card-content">
                        <h3>{classInfo.nom || classInfo.name}</h3>
                        <p>{classInfo.effectif || '0'} élèves</p>
                        <small>Niveau: {classInfo.niveau || 'Non défini'}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="students-list">
              {selectedClass ? (
                <>
                  <div className="students-header">
                    <h2>Élèves de {selectedClass.nom || selectedClass.name}</h2>
                    <div className="search-bar">
                      <input
                        type="text"
                        placeholder="Rechercher un élève..."
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  {loading ? (
                    <div className="loading">Chargement des élèves...</div>
                  ) : (
                    <div className="students-table-container">
                      <table className="students-table">
                        <thead>
                          <tr>
                            <th>Élève</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Inscription</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.length > 0 ? (
                            filteredStudents.map(student => (
                              <tr key={student.id}>
                                <td>
                                  <div className="student-info">
                                    <div className="avatar">{student.nom.charAt(0)}{student.prenom.charAt(0)}</div>
                                    <div>
                                      <p className="student-name">{student.nom} {student.prenom}</p>
                                      <p className="student-id">ID: {student.id}</p>
                                    </div>
                                  </div>
                                </td>
                                <td>{student.email || 'N/A'}</td>
                                <td>{student.telephone || 'N/A'}</td>
                                <td>{new Date(student.created_at).toLocaleDateString('fr-FR')}</td>
                                <td>
                                  <span className={`status-badge ${student.statut || 'actif'}`}>
                                    {student.statut || 'Actif'}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="no-data">Aucun élève trouvé</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <p>Sélectionnez une classe pour voir la liste des élèves</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default TeacherClasses;
